import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';

import type { CampaignAnalysisResult } from '@/entities/campaign/generated/campaign-type';
import {
  formatCompactNumber,
  formatPercent,
  getSummaryCardHeading,
  MAX_REPORT_INSIGHTS_COUNT,
  reportSectionMap,
} from '../../helpers/analysis-report';
import { SectionHeading } from './report-section';

export function ExecutiveSummarySection({ report }: { report: CampaignAnalysisResult }) {
  const executiveSummary = report.executive_summary;
  const summarizedInsightCards = (executiveSummary.key_insight_cards ?? []).slice(
    0,
    MAX_REPORT_INSIGHTS_COUNT,
  );
  const summarizedNextActions = executiveSummary.next_actions ?? [];
  const primaryEngagementRate = report.overview.aggregate.primary_average_engagement_rate;
  const overviewMetrics = [
    {
      label: 'Average view',
      value: formatCompactNumber(report.overview.aggregate.primary_average_views),
    },
    ...(primaryEngagementRate === null
      ? []
      : [
          {
            label: 'Average ER',
            value: formatPercent(primaryEngagementRate),
          },
        ]),
  ];

  return (
    <section
      id={reportSectionMap.executiveSummary.id}
      data-campaign-analysis-print-section
      className="border-border flex scroll-mt-10 flex-col gap-6 border-b px-5 py-10 sm:px-8 sm:py-15"
    >
      <div data-campaign-analysis-print-section-heading className="max-w-3xl">
        <SectionHeading title={reportSectionMap.executiveSummary.label} />
      </div>

      <div
        data-campaign-analysis-print-avoid-break
        className="border-border bg-muted grid gap-8 rounded-xl border p-5 shadow-sm sm:gap-10 sm:p-8 print:gap-5 print:p-5"
      >
        <SummaryRow label={reportSectionMap.overview.label}>
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {overviewMetrics.map((metric) => (
              <SummaryMetric key={metric.label} label={metric.label} value={metric.value} />
            ))}
          </div>
        </SummaryRow>

        <SummaryRow label={reportSectionMap.insights.label}>
          <div className="grid gap-3">
            {summarizedInsightCards.map((card) => (
              <a
                key={card.heading}
                href={`#${reportSectionMap.insights.id}`}
                className="group focus-visible:outline-foreground/30 grid max-w-full cursor-pointer grid-cols-[minmax(0,1fr)_20px] gap-x-3 gap-y-1 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 print:grid-cols-[minmax(0,1fr)]"
              >
                <span className="border-muted-foreground/40 text-muted-foreground col-start-1 row-start-1 inline-flex min-h-6 w-fit max-w-full items-center rounded-full border px-2 py-1 text-xs leading-4 font-medium">
                  {getSummaryCardHeading(card.heading)}
                </span>
                <span className="text-foreground col-start-1 row-start-2 min-w-0 text-sm leading-5 font-semibold break-words underline-offset-4 group-hover:underline">
                  {card.title}
                </span>
                <ArrowRight
                  data-campaign-analysis-summary-link-icon
                  className="text-muted-foreground group-hover:text-foreground col-start-2 row-span-2 row-start-1 size-4 self-center transition group-hover:translate-x-1"
                />
              </a>
            ))}
          </div>
        </SummaryRow>

        <SummaryRow label={reportSectionMap.contentGuide.label}>
          <div className="grid gap-4">
            {summarizedNextActions.map((action, index) => (
              <a
                key={action.action}
                href={`#${reportSectionMap.contentGuide.id}`}
                className="group focus-visible:outline-foreground/30 grid grid-cols-[32px_minmax(0,1fr)_20px] gap-3 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                <span className="text-foreground pt-0.5 text-sm leading-5 font-semibold tabular-nums">
                  {index + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-foreground text-sm leading-5 font-semibold underline-offset-4 group-hover:underline">
                    {action.action}
                  </p>
                  <p className="text-muted-foreground mt-1 text-sm leading-5">{action.effect}</p>
                </div>
                <ArrowRight
                  data-campaign-analysis-summary-link-icon
                  className="text-muted-foreground group-hover:text-foreground mt-0.5 size-4 transition group-hover:translate-x-1"
                />
              </a>
            ))}
          </div>
        </SummaryRow>
      </div>
    </section>
  );
}

function SummaryMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
      <p className="text-foreground text-xl leading-7 font-bold tracking-tight break-words">
        {value}
      </p>
      <p className="text-muted-foreground text-sm leading-5 font-medium">{label}</p>
    </div>
  );
}

function SummaryRow({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div
      data-campaign-analysis-print-avoid-break
      className="grid gap-4 md:grid-cols-[120px_minmax(0,1fr)] md:gap-8 print:grid-cols-[28mm_minmax(0,1fr)] print:gap-4"
    >
      <p className="text-muted-foreground pt-1 text-sm leading-5 font-semibold">{label}</p>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
